import * as React from "react";
import { Box } from "@mui/material";
import type { KitchenArrivalRow } from "./RallyBoardPopup";

type PlayerId = 0 | 1 | 2 | 3;
type Team01 = 0 | 1;

type MiniPoint = {
  shotNumber: number;
  atMsRel: number;
  qualityOverall: number;
  playerId: PlayerId;
};

function miniClamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

function miniNumOrNull(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

function miniIsPlayerId(v: unknown): v is PlayerId {
  return v === 0 || v === 1 || v === 2 || v === 3;
}

function miniTeamOfPlayer(playerId: PlayerId): Team01 {
  return playerId === 0 || playerId === 1 ? 0 : 1;
}

function miniTeamColor(team: Team01, alpha = 1): string {
  return team === 0
    ? `rgba(95,165,255,${alpha})`
    : `rgba(120,225,160,${alpha})`;
}

function buildMiniStripPoints(row: KitchenArrivalRow | null): MiniPoint[] {
  if (!row) return [];

  const overlays = [
    ...(Array.isArray(row.shotNumberOverlays) ? row.shotNumberOverlays : []),
  ].sort((a, b) => (a.rawShot?.start_ms ?? 0) - (b.rawShot?.start_ms ?? 0));

  const out: MiniPoint[] = [];

  for (let i = 0; i < overlays.length; i++) {
    const overlay = overlays[i];
    const q = overlay.rawShot?.quality as
      | { overall?: unknown }
      | undefined;

    const overall = miniNumOrNull(q?.overall);
    if (overall == null) continue;

    const playerIdValue = overlay.playerId ?? overlay.rawShot?.player_id;
    const playerIdNum = Number(playerIdValue);
    if (!miniIsPlayerId(playerIdNum)) continue;

    out.push({
      shotNumber: i + 1,
      qualityOverall: overall,
      playerId: playerIdNum,
      atMsRel:
        typeof overlay.atMsRel === "number"
          ? overlay.atMsRel
          : typeof overlay.rawShot?.start_ms === "number"
            ? overlay.rawShot.start_ms
            : i * 1000,
    });
  }

  return out;
}

export function RallyShotQualityMiniChart(props: {
  row: KitchenArrivalRow | null;
  height?: number;
}) {
  const { row, height = 42 } = props;

  const points = React.useMemo(() => buildMiniStripPoints(row), [row]);

  const width = 180;

  if (!points.length) {
    return (
      <Box
        sx={{
          width: "100%",
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 11,
          color: "rgba(0,0,0,0.35)",
          minWidth: 0,
        }}
      >
        —
      </Box>
    );
  }

  const servingTeam =
    row?.servingTeam === 0 || row?.servingTeam === 1 ? row.servingTeam : null;

  const padLeft = 6;
  const padRight = 6;
  const padTop = 5;
  const padBottom = 5;

  const innerW = width - padLeft - padRight;
  const innerH = height - padTop - padBottom;

  const minMs = Math.min(...points.map((p) => p.atMsRel));
  const maxMs = Math.max(...points.map((p) => p.atMsRel));

  const xForMs = (ms: number) =>
    padLeft + ((ms - minMs) / Math.max(1, maxMs - minMs)) * innerW;

  const yFor = (quality: number) =>
    padTop + (1 - miniClamp(quality, 0, 1)) * innerH;

  const plotted = points.map((p) => ({
    ...p,
    x: xForMs(p.atMsRel),
    y: yFor(p.qualityOverall),
  }));

  const pathD = plotted
    .map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`)
    .join(" ");

  return (
    <Box
      sx={{
        width: "100%",
        height,
        minWidth: 0,
        overflow: "hidden",
      }}
    >
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        style={{
          display: "block",
          width: "100%",
          height: "100%",
        }}
      >
        <path
          d={pathD}
          fill="none"
          stroke="rgba(0,0,0,0.34)"
          strokeWidth="1.35"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {plotted.map((p, idx) => {
          const isFirst = idx === 0;
          const isLast = idx === plotted.length - 1;
          const hitterTeam = miniTeamOfPlayer(p.playerId);

          let fill = miniTeamColor(hitterTeam); // "rgba(0,0,0,0.52)";
          let r = 3.0; //1.9;

          // if (isFirst) {
          //   fill =
          //     servingTeam == null
          //       ? "rgba(0,0,0,0.62)"
          //       : miniTeamColor(servingTeam, 0.98);
          //   //r = 3.3;
          // } else if (isLast) {
          //   fill = miniTeamColor(hitterTeam, 0.98);
          //   //r = 3.3;
          // }

          return (
            <circle
              key={`${p.shotNumber}_${idx}`}
              cx={p.x}
              cy={p.y}
              r={r}
              fill={fill}
            />
          );
        })}
      </svg>
    </Box>
  );
}




